import moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";

import { useCurrentUser } from "src/core/users/hooks";
import { OrderDates } from "src/library/components/DayPicker/OrderDayPicker";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import { isSameDay } from "src/library/utils";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Button } from "src/library/components/Button";

interface ConfirmMultipleOrderModalProps extends Pick<BasicModalProps, "onDismiss" | "open">{
  orderDates: OrderDates;
  onConfirm: () => void;
  selectedDay: Date;
  terminology?: ITerminology;
}

/**
 * A modal to be used when a customer is attempting to place an order on a delivery date that already has
 * at least one existing order. The modal asks the user to confirm they're happy to place another order, whilst
 * offering the option to view the existing order / orders.
 */
const ConfirmMultipleOrderModal: React.ReactFCWithChildren<ConfirmMultipleOrderModalProps> = ({
  open, onConfirm, onDismiss, orderDates, selectedDay, terminology
}) => {
  const [ { currentRole } ] = useCurrentUser();
  const [ hasMultiple, setHasMultiple ] = React.useState(false);

  // handles what page to redirect to
  const handleViewClick = React.useCallback(() => {
    const matchingOrders = orderDates ? orderDates.filter(dateOrder => isSameDay(dateOrder.date, selectedDay)) : [];
    const urlRoute = `${currentRole?.type === "admin" && matchingOrders.length > 1 ? "/admin/" : "/"}orders`;

    setHasMultiple(matchingOrders.length > 1);

    if (matchingOrders.length > 0) {
      // if only one order, direct to the order
      if (matchingOrders.length === 1) {
        return window.open(`${urlRoute}/${matchingOrders[ 0 ].id}`, "_blank");
      }

      // if more than one order, direct to the orders pages with filter applied
      if (matchingOrders.length > 1) {
        return window.open(`${urlRoute}?filters=deliveryDate:${moment.utc(matchingOrders[ 0 ].date).format("YYYY-MM-DD")}`, "_blank");
      }
    }

    return;
  }, [
    currentRole?.type,
    orderDates,
    selectedDay
  ]);

  return (
    <StyledBasicModal
      open={open}
      onDismiss={() => onDismiss()}
      title={`${hasMultiple ? `Multiple ${terminology?.order.plural.text}` : `An ${terminology?.order.singular.text}`} already exists.`}
    >
      <Text as="p">
        {`You already have ${hasMultiple ? `multiple ${terminology?.order.plural.text}` : `an ${terminology?.order.singular.text}`} for the delivery date of ${moment.utc(selectedDay).format("DD/MM/YYYY")}.
      Are you sure you would like to create another ${terminology?.order.singular.text} for the same date?`}
      </Text>

      <Text as="p">
        {"Alternatively, "}

        <span
          onClick={() => handleViewClick()}
          className="link-text"
        >
          View Orders
        </span>

        {hasMultiple ? ` to select an ${terminology?.order.singular.text} to amend.` : " to check products and edit if required."}
      </Text>

      <div className="actions">
        <Button
          $buttonType="secondary"
          onClick={() => onDismiss()}
        >
          Cancel
        </Button>

        <Button
          onClick={() => {
            onConfirm();
            onDismiss();
          }}
        >
          Add Another Order
        </Button>
      </div>
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  ${({ theme }) => css`
    .link-text {
      cursor: pointer;
      color: #0077be;
      text-decoration: underline;
    }

    .actions {
      display: flex;
      justify-content: space-between;
      margin-top: ${theme.spacing.margins.lg};
    }
  `}
`;

export default ConfirmMultipleOrderModal;
